(() => {
  'use strict';

  const FIELDS = ['spread', 'crown', 'height', 'roots', 'lifespan', 'biodiversity', 'carbon'];
  const PREFIX = 'ho_tree_';
  function resetFilterUrl(input) {
    const url = new URL(input);
    for (const key of [...url.searchParams.keys()]) {
      if ((key.startsWith('filter.') && key !== 'filter.p.product_type') || key.startsWith(PREFIX) || key === 'page') url.searchParams.delete(key);
    }
    return url;
  }

  if (typeof module !== 'undefined' && module.exports && typeof document === 'undefined') {
    module.exports = { resetFilterUrl };
    return;
  }
  if (window.HarrodTreeFilters) return;

  const originals = new WeakMap();
  const openFields = new Map();
  let grid = null;
  let gridMarker = null;
  let gridState = {};
  let cards = [];
  let definitions = [];
  let generation = 0;
  let queued = false;
  let complete = false;
  let enabled = false;
  let renderedNativeSignature = '';

  function nativeSignature(url = new URL(window.location.href)) {
    const entries = Array.from(url.searchParams.entries()).filter(([key]) => key.startsWith('filter.') || key === 'sort_by' || key === 'q').sort(([ka, va], [kb, vb]) => ka.localeCompare(kb) || va.localeCompare(vb));
    return url.pathname + JSON.stringify(entries);
  }

  function selection(url = new URL(window.location.href)) {
    return new Map(FIELDS.map(key => [key, new Set(url.searchParams.getAll(PREFIX + key)
      .flatMap(value => value.split(','))
      .filter(value => /^[a-z0-9][a-z0-9-]*$/.test(value)))]));
  }

  function selectedCount(state) {
    return Array.from(state.values()).reduce((total, values) => total + values.size, 0);
  }

  function matches(card, state, omittedField) {
    if (!selectedCount(state)) return true;
    if (!card.data.eligible) return false;
    return FIELDS.every(key => key === omittedField || state.get(key).size === 0 ||
      Array.from(state.get(key)).some(value => (card.data.fields[key] || []).includes(value)));
  }

  function removeTreeParameters(url) {
    FIELDS.forEach(key => url.searchParams.delete(PREFIX + key));
    url.searchParams.delete(PREFIX + 'page');
  }

  function navigateState(state, page = 1) {
    const url = new URL(window.location.href);
    removeTreeParameters(url);
    url.searchParams.delete('page');
    FIELDS.forEach(key => state.get(key).forEach(value => url.searchParams.append(PREFIX + key, value)));
    if (page > 1) url.searchParams.set(PREFIX + 'page', String(page));
    if (url.href !== window.location.href) window.history.pushState({ ...window.history.state, hoTree: true }, '', url);
    apply();
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function updateRecovery(panel) {
    const current = new URL(window.location.href);
    const reset = resetFilterUrl(current.href);
    const canClear = reset.href !== current.href;
    const clear = panel.querySelector('[data-ho-filter-reset]');
    const browse = panel.querySelector('[data-ho-browse-products]');
    if (clear) { clear.href = reset.href; clear.hidden = !canClear; }
    if (browse) browse.hidden = canClear;
    const title = panel.querySelector('[data-ho-empty-title]');
    const description = panel.querySelector('[data-ho-empty-description]');
    if (title) title.textContent = canClear ? 'No products match these filters.' : 'No products are available in this selection.';
    if (description) description.textContent = canClear ? 'Clear the filters to see products in your chosen category or style.' : 'Browse all products to explore another category or style.';
  }

  function humanize(value) {
    return value.replace(/-/g, ' ').replace(/^./, char => char.toUpperCase());
  }

  function orderOptions(key, options) {
    if (key === 'spread') {
      const labels = {
        'small-spread': 'Small · up to 4 m',
        'medium-spread': 'Medium · over 4–8 m',
        'large-spread': 'Large · over 8–12 m',
        'extra-large-spread': 'Extra large · over 12 m'
      };
      options.forEach(option => { if (labels[option.value]) option.label = labels[option.value]; });
    }
    const orders = {
      spread: ['small', 'medium', 'large', 'extra-large'],
      height: ['small', 'medium', 'large', 'extra-large'],
      crown: ['light', 'half-dense', 'dense'],
      lifespan: ['short', 'medium', 'long'],
      biodiversity: ['excellent', 'good', 'moderate', 'limited', 'not-rated'],
      carbon: ['small', 'medium', 'large', 'not-rated']
    };
    const order = orders[key];
    if (!order) return options.sort((a, b) => a.label.localeCompare(b.label));
    const rank = value => {
      for (let index = order.length - 1; index >= 0; index--) {
        if (value === order[index] || value.startsWith(order[index] + '-')) return index;
      }
      return order.length;
    };
    return options.sort((a, b) => rank(a.value) - rank(b.value) || a.label.localeCompare(b.label));
  }

  function buildPanel(panel) {
    if (panel.dataset.hoTreeGeneration === String(generation) && panel.querySelector('[data-ho-tree-option]')) return;
    const container = panel.querySelector('[data-ho-tree-fields]');
    if (!container) return;
    container.replaceChildren();
    const state = selection();
    definitions.forEach(definition => {
      const details = element('details');
      details.dataset.hoTreeField = definition.key;
      details.open = openFields.get(definition.key) === true || state.get(definition.key).size > 0;
      const summary = element('summary', '', definition.label);
      details.append(summary);
      if (definition.help) details.append(element('p', 'ho-tree-filters__help', definition.help));
      const options = element('div', 'ho-tree-filters__options');
      options.setAttribute('role', 'group');
      options.setAttribute('aria-label', definition.label);
      definition.options.forEach(option => {
        const button = element('button', 'ho-tree-filters__option');
        button.type = 'button';
        button.dataset.hoTreeOption = definition.key;
        button.dataset.hoTreeValue = option.value;
        button.setAttribute('aria-pressed', 'false');
        if (option.icon) {
          const image = element('img');
          image.src = option.icon;
          image.alt = '';
          image.width = 28;
          image.height = 28;
          image.loading = 'lazy';
          button.append(image);
        }
        button.append(element('span', '', option.label));
        const count = element('span');
        count.dataset.hoTreeOptionCount = '';
        count.setAttribute('aria-hidden', 'true');
        button.append(count);
        options.append(button);
      });
      details.append(options);
      container.append(details);
    });
    panel.dataset.hoTreeGeneration = String(generation);
  }

  function statusElements() {
    const wrapper = grid.closest('.productgrid--wrapper') || grid.parentElement;
    let status = wrapper.querySelector('[data-ho-tree-results]');
    if (!status) {
      status = element('div', 'ho-tree-results');
      status.dataset.hoTreeResults = '';
      const text = element('span');
      text.dataset.hoTreeResultText = '';
      text.setAttribute('role', 'status');
      text.setAttribute('aria-live', 'polite');
      text.setAttribute('aria-atomic', 'true');
      status.append(text);
      const clear = element('button', '', 'Clear tree filters');
      clear.type = 'button';
      clear.dataset.hoTreeClear = '';
      status.append(clear);
      grid.before(status);
    }
    let pagination = wrapper.querySelector('[data-ho-tree-pagination]');
    if (!pagination) {
      pagination = element('nav', 'ho-tree-pagination');
      pagination.dataset.hoTreePagination = '';
      pagination.setAttribute('aria-label', 'Tree results pages');
      grid.after(pagination);
    }
    let empty = wrapper.querySelector('[data-ho-tree-empty]');
    if (!empty) {
      empty = element('div', 'ho-filter-empty');
      empty.dataset.hoTreeEmpty = '';
      empty.dataset.hoFilterEmpty = '';
      const title = element('h2', 'ho-filter-empty__title');
      title.dataset.hoEmptyTitle = '';
      const description = element('p', 'ho-filter-empty__description');
      description.dataset.hoEmptyDescription = '';
      const clear = element('a', 'ho-filter-empty__action', 'Clear filters');
      clear.dataset.hoFilterReset = '';
      const browse = element('a', 'ho-filter-empty__action', 'Browse all products');
      browse.dataset.hoBrowseProducts = '';
      browse.href = gridState.browseUrl || '/collections/all';
      empty.append(title, description, clear, browse);
      grid.after(empty);
    }
    updateRecovery(empty);
    return { status, pagination, empty };
  }

  function updateNativeLabels(hideCounts) {
    document.querySelectorAll('.collection-filters__filter-list-item-text').forEach(node => {
      if (!originals.has(node)) originals.set(node, node.textContent);
      const text = originals.get(node);
      const next = hideCounts ? text.replace(/\s*\([\d,]+\)\s*$/, '') : text;
      if (node.textContent !== next) node.textContent = next;
    });
  }

  function updateNativeLinks(state) {
    document.querySelectorAll('a[data-filter-input]').forEach(link => {
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      removeTreeParameters(url);
      FIELDS.forEach(key => state.get(key).forEach(value => url.searchParams.append(PREFIX + key, value)));
      link.href = url.pathname + url.search + url.hash;
      if (link.hasAttribute('data-url')) {
        const path = url.pathname + url.search;
        link.dataset.url = link.dataset.url.startsWith('%') ? encodeURIComponent(path) : path;
      }
    });
  }

  function renderPagination(container, currentPage, totalPages) {
    const signature = currentPage + ':' + totalPages;
    if (container.dataset.hoTreePages === signature) return;
    container.replaceChildren();
    const addButton = (label, targetPage, disabled, current) => {
      const button = element('button', '', label);
      button.type = 'button';
      button.dataset.hoTreePage = String(targetPage);
      button.disabled = disabled;
      button.setAttribute('aria-label', /^\d+$/.test(label) ? 'Page ' + label : label + ' page');
      if (current) button.setAttribute('aria-current', 'page');
      container.append(button);
    };
    addButton('Previous', currentPage - 1, currentPage === 1, false);
    for (let page = 1; page <= totalPages; page++) addButton(String(page), page, false, page === currentPage);
    addButton('Next', currentPage + 1, currentPage === totalPages, false);
    container.dataset.hoTreePages = signature;
  }

  function apply() {
    const panels = Array.from(document.querySelectorAll('[data-ho-tree-panel]'));
    panels.forEach(panel => { panel.hidden = !enabled; });
    if (!enabled || !grid) {
      document.querySelectorAll('[data-ho-tree-results], [data-ho-tree-pagination], [data-ho-tree-empty]').forEach(node => { node.hidden = true; });
      document.querySelectorAll('[data-ho-tree-native-pagination-hidden]').forEach(node => node.removeAttribute('data-ho-tree-native-pagination-hidden'));
      updateNativeLabels(false);
      return;
    }

    const state = selection();
    const activeCount = selectedCount(state);
    panels.forEach(panel => {
      buildPanel(panel);
      const notice = panel.querySelector('[data-ho-tree-notice]');
      if (notice) notice.hidden = complete;
      const clear = panel.querySelector('[data-ho-tree-clear]');
      if (clear) clear.hidden = !activeCount;
      const heading = panel.querySelector('.ho-tree-filters__heading strong');
      if (heading) heading.textContent = 'Tree size & ecology' + (activeCount ? ' (' + activeCount + ')' : '');
      panel.querySelectorAll('[data-ho-tree-option]').forEach(button => {
        const key = button.dataset.hoTreeOption;
        const value = button.dataset.hoTreeValue;
        const selected = state.get(key).has(value);
        const count = complete ? cards.filter(card => card.data.eligible && matches(card, state, key) &&
          (card.data.fields[key] || []).includes(value)).length : 0;
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = !complete || (!selected && count === 0);
        const countNode = button.querySelector('[data-ho-tree-option-count]');
        if (countNode) countNode.textContent = complete ? '(' + count + ')' : '';
        button.setAttribute('aria-label', button.querySelector('span').textContent + (complete ? ', ' + count + ' trees' : ''));
      });
    });

    const ui = statusElements();
    if (!complete) {
      ui.status.hidden = true;
      ui.pagination.hidden = true;
      ui.empty.hidden = true;
      cards.forEach(card => {
        card.node.removeAttribute('data-ho-tree-filter-hidden');
        card.node.removeAttribute('data-ho-tree-page-hidden');
      });
      document.querySelectorAll('[data-ho-native-pagination]').forEach(node => node.removeAttribute('data-ho-tree-native-pagination-hidden'));
      updateNativeLabels(false);
      return;
    }

    const matched = cards.filter(card => matches(card, state));
    const perPage = Number(gridState.pageSize) || 24;
    const totalPages = Math.max(1, Math.ceil(matched.length / perPage));
    const requestedPage = Math.trunc(Number(new URL(window.location.href).searchParams.get(PREFIX + 'page'))) || 1;
    const page = Math.min(totalPages, Math.max(1, requestedPage));
    const onPage = new Set(matched.slice((page - 1) * perPage, page * perPage));
    const allMatches = new Set(matched);
    cards.forEach(card => {
      card.node.toggleAttribute('data-ho-tree-filter-hidden', !allMatches.has(card));
      card.node.toggleAttribute('data-ho-tree-page-hidden', !onPage.has(card));
    });
    ui.status.hidden = false;
    ui.status.querySelector('[data-ho-tree-result-text]').textContent = matched.length +
      (activeCount ? ' matching ' : ' ') + (matched.length === 1 ? 'tree' : 'trees');
    ui.status.querySelector('[data-ho-tree-clear]').hidden = !activeCount || matched.length === 0;
    ui.empty.hidden = matched.length > 0;
    ui.pagination.hidden = totalPages < 2;
    renderPagination(ui.pagination, page, totalPages);
    document.querySelectorAll('[data-ho-native-pagination]').forEach(node => node.setAttribute('data-ho-tree-native-pagination-hidden', ''));
    updateNativeLabels(activeCount > 0);
    updateNativeLinks(state);
  }

  function refresh() {
    queued = false;
    document.querySelectorAll('[data-ho-filter-empty]').forEach(updateRecovery);
    const nextGrid = document.querySelector('[data-productgrid-items][data-ho-tree-mode]');
    const nextMarker = nextGrid?.querySelector('[data-ho-tree-grid-state]');
    const changed = nextGrid !== grid || nextMarker !== gridMarker;
    grid = nextGrid;
    gridMarker = nextMarker;
    try { gridState = JSON.parse(gridMarker?.textContent || '{}'); } catch (_) { gridState = {}; }
    enabled = !!grid && gridState.enabled === true;
    if (!enabled) { apply(); return; }
    if (new URL(window.location.href).searchParams.has('page') && Number(gridState.total) <= 250) {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      window.location.replace(url.href);
      return;
    }
    if (changed) {
      renderedNativeSignature = nativeSignature();
      generation++;
      cards = Array.from(grid.querySelectorAll(':scope > [data-ho-tree-product]')).map(node => {
        try {
          const data = JSON.parse(node.dataset.hoTreeProduct);
          if (!data.fields || typeof data.eligible !== 'boolean') return null;
          FIELDS.forEach(key => { data.fields[key] = (data.fields[key] || []).filter(value => typeof value === 'string' && value.length); });
          return { node, data };
        } catch (_) { return null; }
      }).filter(Boolean);
      complete = cards.length === Number(gridState.total) && cards.length <= 250;
      const schema = document.querySelector('[data-ho-tree-options]');
      try { definitions = JSON.parse(schema?.textContent || '[]'); } catch (_) { definitions = []; complete = false; }
      const state = selection();
      definitions.forEach(definition => {
        const present = new Set(definition.options.map(option => option.value));
        const values = new Set(cards.flatMap(card => card.data.fields[definition.key] || []));
        state.get(definition.key).forEach(value => values.add(value));
        values.forEach(value => {
          if (!present.has(value)) definition.options.push({ value, label: humanize(value), icon: '' });
        });
        orderOptions(definition.key, definition.options);
      });
    }
    apply();
  }

  function queueRefresh() {
    if (!queued) { queued = true; window.requestAnimationFrame(refresh); }
  }

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const option = target.closest('[data-ho-tree-option]');
    if (option && enabled && complete) {
      event.preventDefault();
      const state = selection();
      const values = state.get(option.dataset.hoTreeOption);
      const value = option.dataset.hoTreeValue;
      if (values.has(value)) values.delete(value); else values.add(value);
      navigateState(state);
      return;
    }
    if (target.closest('[data-ho-tree-clear]')) {
      event.preventDefault();
      navigateState(new Map(FIELDS.map(key => [key, new Set()])));
      return;
    }
    const page = target.closest('[data-ho-tree-page]');
    if (page && enabled && complete) {
      event.preventDefault();
      navigateState(selection(), Number(page.dataset.hoTreePage));
      grid.setAttribute('tabindex', '-1');
      grid.focus({ preventScroll: true });
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (!enabled || !complete || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const hasLocalState = selectedCount(selection()) > 0 || new URL(window.location.href).searchParams.has(PREFIX + 'page');
    const sortButton = target.closest('[data-productgrid-trigger-sort-button]');
    if (sortButton && hasLocalState) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortButton.value);
      url.searchParams.delete('page');
      url.searchParams.delete(PREFIX + 'page');
      window.location.assign(url.href);
      return;
    }
    if (!selectedCount(selection())) return;
    const link = target.closest('a[data-filter-input], a.filter-item-clear-all');
    if (!link) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    url.searchParams.delete('page');
    url.searchParams.delete(PREFIX + 'page');
    removeTreeParameters(url);
    if (!link.matches('.filter-item-clear-all')) {
      const state = selection();
      FIELDS.forEach(key => state.get(key).forEach(value => url.searchParams.append(PREFIX + key, value)));
    }
    window.location.assign(url.href);
  }, true);

  document.addEventListener('change', event => {
    if (!enabled || !complete) return;
    if (!selectedCount(selection()) && !new URL(window.location.href).searchParams.has(PREFIX + 'page')) return;
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[name="sort_by"], [data-sortby-select], .utils-sortby-select')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    url.searchParams.delete(PREFIX + 'page');
    window.location.assign(url.href);
  }, true);

  document.addEventListener('toggle', event => {
    const details = event.target;
    if (details instanceof Element && details.matches('details[data-ho-tree-field]')) openFields.set(details.dataset.hoTreeField, details.open);
  }, true);

  window.addEventListener('popstate', event => {
    if (enabled && complete && nativeSignature() === renderedNativeSignature) event.stopImmediatePropagation();
    queueRefresh();
  }, true);
  document.addEventListener('shopify:section:load', queueRefresh);
  const observer = new MutationObserver(records => {
    if (records.some(record => Array.from(record.addedNodes).some(node => node instanceof Element &&
      (node.matches('[data-productgrid-items], [data-ho-tree-panel], [data-ho-tree-grid-state], [data-ho-filter-empty], a[data-filter-input]') || node.querySelector('[data-productgrid-items], [data-ho-tree-panel], [data-ho-tree-grid-state], [data-ho-filter-empty], a[data-filter-input]'))))) queueRefresh();
  });

  window.HarrodTreeFilters = { refresh: queueRefresh };
  const init = () => { observer.observe(document.body, { childList: true, subtree: true }); refresh(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
